'use client';

import './index.css';

import { storyblokEditable } from '@storyblok/react';
import classnames from 'classnames';
import { useState } from 'react';

import { Button } from '@/components/button';
import { MaxWidth } from '@/components/max-width';

import { hasText, render } from '@/helpers/storyblok/rich-text';
import { markdownToHtml } from '@/helpers/string';

const PERIODS = {
  MONTHLY: 'monthly',
  ANNUALLY: 'annually',
};

export const PricingPlansClient = ({ blok }) => {
  const [period, setPeriod] = useState(PERIODS.ANNUALLY);

  const items = blok.items || [];
  const isAnnual = period === PERIODS.ANNUALLY;

  return (
    <div {...storyblokEditable(blok)} className="pricing-plans">
      <MaxWidth>
        <div className="pricing-plans__toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!isAnnual}
            className={classnames('pricing-plans__toggle__option', {
              'pricing-plans__toggle__option--active': !isAnnual,
            })}
            onClick={() => setPeriod(PERIODS.MONTHLY)}
          >
            <span
              className="pricing-plans__toggle__option__label"
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(blok.monthlyLabel || 'Mensuel'),
              }}
            />
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={isAnnual}
            className={classnames('pricing-plans__toggle__option', {
              'pricing-plans__toggle__option--active': isAnnual,
            })}
            onClick={() => setPeriod(PERIODS.ANNUALLY)}
          >
            <span
              className="pricing-plans__toggle__option__label"
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(blok.annuallyLabel || 'Annuel'),
              }}
            />
          </button>
        </div>

        <ul className="pricing-plans__grid">
          {items.map((item) => {
            const hasTag = Boolean(item.tag);
            const price = isAnnual ? item.annuallyPrice : item.monthlyPrice;
            const priceNote = isAnnual ? item.annuallyNote : item.monthlyNote;
            const footer = isAnnual
              ? item.annuallyFooter
              : item.monthlyFooter;

            return (
              <li
                key={item._uid}
                {...storyblokEditable(item)}
                className={classnames('pricing-plans__item', {
                  'pricing-plans__item--featured': hasTag,
                })}
              >
                {hasTag ? (
                  <span className="pricing-plans__item__tag">{item.tag}</span>
                ) : null}

                <div className="pricing-plans__item__header">
                  {item.title ? (
                    <p
                      className="pricing-plans__item__title"
                      dangerouslySetInnerHTML={{
                        __html: markdownToHtml(item.title),
                      }}
                    />
                  ) : null}

                  {hasText(item.description) ? (
                    <div className="pricing-plans__item__description">
                      {render(item.description)}
                    </div>
                  ) : null}
                </div>

                <div className="pricing-plans__item__body">
                  {price || priceNote ? (
                    <div className="pricing-plans__item__price">
                      {price ? (
                        <p className="pricing-plans__item__price__value">
                          {price}
                        </p>
                      ) : null}

                      {priceNote ? (
                        <p
                          className="pricing-plans__item__price__note"
                          dangerouslySetInnerHTML={{
                            __html: markdownToHtml(priceNote),
                          }}
                        />
                      ) : null}
                    </div>
                  ) : null}

                  {hasText(item.content) ? (
                    <>
                      <span
                        className="pricing-plans__item__divider"
                        aria-hidden="true"
                      />

                      <div className="pricing-plans__item__content">
                        {render(item.content)}
                      </div>
                    </>
                  ) : null}
                </div>

                <div className="pricing-plans__item__footer">
                  {item.cta?.length > 0 ? (
                    <div className="pricing-plans__item__cta">
                      {item.cta.map((link) => (
                        <Button
                          as="button"
                          key={link._uid}
                          {...storyblokEditable(link)}
                          theme={hasTag ? 'primary' : 'outline-primary'}
                          link={link.link}
                        >
                          {link.title}
                        </Button>
                      ))}
                    </div>
                  ) : null}

                  {footer ? (
                    <p
                      className="pricing-plans__item__footer__note"
                      dangerouslySetInnerHTML={{
                        __html: markdownToHtml(footer),
                      }}
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </MaxWidth>
    </div>
  );
};
